import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import type { AxiosRequestConfig } from 'axios'
import type { SimConfigTone } from './simConfig'
import type { CheckoutPaymentMethod } from '~/lib/utils/payment-methods'

import { deleteRequest, getRequest, postRequest } from '@lib/util/http'

import { BACKEND_URL } from '~/lib/config'

// Controller
export const cartController = 'cart/'

// Shipping Address interface based on cartStore.ts Address interface
export interface ShippingAddress {
  locale?: string
  firstName: string
  lastName: string
  companyName?: string
  nifCifNie: string
  street: string
  number?: string
  blockFloorDoor?: string
  city?: string
  country?: string
  province: string
  island?: string
  postalCode: string
  countryPrefix: string
  phone: string
  email: string
  orderNotes?: string
  projectRef?: string
  differentBillingAddress: boolean
  // Billing address fields (only used when differentBillingAddress is true)
  billingFirstName?: string
  billingLastName?: string
  billingCompanyName?: string
  billingNifCifNie?: string
  billingStreet?: string
  billingNumber?: string
  billingBlockFloorDoor?: string
  billingCity?: string
  billingCountry?: string
  billingIsland?: string
  billingProvince?: string
  billingPostalCode?: string
  billingCountryPrefix?: string
  billingPhone?: string
  billingEmail?: string
  termsAndConditions: boolean
  receiveOffers: boolean
}

// Interfaces
export interface CartItemTonePostData {
  toneId: string
  area: string
}

export interface CartItemPostData {
  designId?: string
  colors?: CartItemTonePostData[]
  thickness?: string
  amount: number
  includeProtection?: boolean
  includeJoint?: boolean
  itemType?: string
  itemId?: string
}

export interface MaterialToneConfigurationResponseData {
  tone: SimConfigTone
  area: string
}

export type JsonPrimitive = string | number | boolean | null

export type JsonValue
  = | JsonPrimitive
    | JsonValue[]
    | { [key: string]: JsonValue | undefined }

export interface CartItemShapeBase {
  [key: string]: JsonValue | undefined
}

export interface PlanterCartItemShape extends CartItemShapeBase {
  diameter?: number | null
  height?: number | null
  surface?: string | null
}

export interface LatticeCartItemShape extends CartItemShapeBase {
  thickness?: number | string | null
  height?: number | null
  width?: number | null
}

export interface ZelligeCartItemShape extends CartItemShapeBase {
  thickness?: number | string | null
  size?: string | null
}

export interface BarroAltaCartItemShape extends CartItemShapeBase {
  thickness?: number | string | null
}

export interface CartItemSnapshotBase {
  reference?: string | null
  label?: string | null
  tone?: string | null
  price?: string | number | null
  unit?: string | null
  shape?: CartItemShapeBase | null
  [key: string]: JsonValue | undefined
}

export interface PlanterCartItemSnapshot extends CartItemSnapshotBase {
  shape?: PlanterCartItemShape | null
}

export interface LatticeCartItemSnapshot extends CartItemSnapshotBase {
  shape?: LatticeCartItemShape | null
  pieces?: number | null
  pieces_per_m2_value?: number | null
  rounded_amount?: number | null
}

export interface ZelligeCartItemSnapshot extends CartItemSnapshotBase {
  shape?: ZelligeCartItemShape | null
  boxes?: number | null
  m2_per_box?: number | null
  rounded_amount?: number | null
}

export interface BarroAltaCartItemSnapshot extends CartItemSnapshotBase {
  shape?: BarroAltaCartItemShape | null
}

export interface MuestraCartItemSnapshot extends CartItemSnapshotBase {
  shape?: CartItemShapeBase | null
}

export type CartItemSnapshot
  = | CartItemSnapshotBase
    | PlanterCartItemSnapshot
    | LatticeCartItemSnapshot
    | ZelligeCartItemSnapshot
    | BarroAltaCartItemSnapshot
    | MuestraCartItemSnapshot
    | null

// Use the complete Design interface from design.ts
export interface CartItemResponsePostData {
  id: string
  short_name?: string
  image_url?: string
  thickness?: string
  colors?: string[]
  description?: string
  amount: number
  // Pricing fields that should come from backend
  unit_price: number
  total_price: number
  // Item type and data fields
  item_type?: string
  item_id?: number
  item_data?: CartItemSnapshot
  // Optional pricing for joint and protection (only for design items)
  joint_price?: number
  protection_price?: number
  // New fields for dynamic SVG coloring (only for design items)
  svg_url?: string
  tones?: Array<{ id: string, hex: string, area: string }>
  // Fields for unit display (m² vs ud)
  area?: number | null // For products sold by m² (Zellige, Barro Alta)
  unit?: string | null // 'ud' for units, 'm2' for square meters
  availability?: string | null
}

// Transport pallet data
export interface TransportPalletData {
  type: string
  weight: number
  count: number
}

// Transport option data
export interface TransportOptionData {
  carrier: string
  location: string
  zone: string
  total_weight: number
  current_weight: number
  price: number
  total_price: number
  pallets: TransportPalletData[]
}

// Cart summary data that should be calculated by backend
export interface CartSummaryData {
  subtotal: number
  vat_rate: number
  shipping: number | null
  vat_amount: number | null
  total_with_vat: number | null
  total_with_vat_no_shipping: number | null
  payment_confirmation_amount: number | null
  shipping_details: TransportOptionData | null
  shipping_error?: boolean
}

export interface CartItemsResponse {
  items: CartItemResponsePostData[]
  summary: CartSummaryData | null
  locations: string[]
  provinces: string[]
  country_provinces?: Record<string, string[]>
  province_mapping?: Record<string, string[]>
  allowed_shipping_countries?: string[]
  transfer_iban: string
  terms_and_conditions_url: string
  shipping_error?: boolean
  available_payment_methods: CheckoutPaymentMethod[]
}

export interface CartQuoteRequestPostData {
  first_name: string
  last_name: string
  email: string
  locale?: string
  phone?: string
  street?: string
  number?: string
  postal_code?: string
  country: string
  province: string
  locality: string
}

export interface CartQuoteRequestResponseData {
  email: string
}

export interface CartQuoteRequestResponse {
  data: CartQuoteRequestResponseData
}

// API Functions
export async function getUserCart(location?: string, country?: string, island?: string, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(cartController, BACKEND_URL)
  if (location) {
    fetchURL.searchParams.set('location', location)
  }
  if (country) {
    fetchURL.searchParams.set('country', country)
  }
  if (island) {
    fetchURL.searchParams.set('island', island)
  }
  return getRequest<{ data: CartItemsResponse }>(fetchURL.href, axiosConfig)
}

// Definir la interfaz para la respuesta de addCartItem
export interface AddCartItemResponse {
  success: boolean
  message?: string
}

export async function addCartItem(data: CartItemPostData, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(`${cartController}add-item`, BACKEND_URL)
  return postRequest<AddCartItemResponse, CartItemPostData>(
    fetchURL.href,
    data,
    axiosConfig,
  )
}

export async function sendCartQuoteRequest(data: CartQuoteRequestPostData, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(`${cartController}quote-request`, BACKEND_URL)
  const headers = data.locale
    ? {
        ...axiosConfig.headers,
        'Accept-Language': data.locale,
      }
    : axiosConfig.headers
  return postRequest<CartQuoteRequestResponse, CartQuoteRequestPostData>(
    fetchURL.href,
    data,
    {
      ...axiosConfig,
      headers,
    },
  )
}

export async function deleteCartItem(itemId: string) {
  const fetchURL = new URL(`${cartController}cart-item/${itemId}`, BACKEND_URL)
  return deleteRequest<CartItemsResponse>(fetchURL.href)
}

// Pricing interfaces
export interface UnitPriceGetData {
  designId: string
  colors: {
    toneId: string
    area: string
  }[]
  thickness: string
}

export interface PriceResponseData {
  unit_price: number | null
  availability: string | null
  is_error?: boolean
  error_message?: string
}

export interface PriceResponse {
  data: PriceResponseData
}

// Pricing API functions
export async function getItemUnitPrice(data: UnitPriceGetData, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(`${cartController}item-unit-price`, BACKEND_URL)
  return postRequest<PriceResponse, UnitPriceGetData>(
    fetchURL.href,
    data,
    axiosConfig,
  )
}

// Order interfaces
export interface OrderResponseData {
  id: string
  order_number: string
  status: string
  customer_email: string
  customer_phone?: string
  shipping_address: ShippingAddress
  subtotal: number
  shipping_cost: number
  tax_amount: number
  total_amount: number
  order_date: string
  payment_date?: string
  payment_status?: string
  payment_reference?: string
  notes?: string
  items: CartItemResponsePostData[]
  crm_code?: string
  payment_method?: CheckoutPaymentMethod | null
}

export interface OrdersListResponseData {
  orders: OrderResponseData[]
}

export interface OrderResponse {
  data: OrderResponseData
}

export interface OrdersListResponse {
  data: OrdersListResponseData
}

export interface AdminOrderResponseData {
  uuid: string
  cart_id?: number | null
  user_id?: number | null
  order_number: string
  status: string
  payment_status?: string | null
  payment_reference?: string | null
  payment_method?: CheckoutPaymentMethod | null
  customer_email: string
  customer_phone?: string | null
  shipping_address: ShippingAddress
  subtotal: number
  shipping_cost: number
  tax_amount: number
  total_amount: number
  order_date?: string | null
  payment_date?: string | null
  notes?: string | null
  crm_order_id?: string | null
}

export interface AdminOrdersListResponseData {
  orders: AdminOrderResponseData[]
  current_page: number
  total_pages: number
  pageIndex: number
  pageSize: number
}

export interface AdminOrdersListResponse {
  data: AdminOrdersListResponseData
}

export interface AdminOrdersQueryFilters {
  sorting?: SortingState
  columnFilters?: ColumnFiltersState
  pagination?: PaginationState
}

// Order API functions
export async function getUserOrders(axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(`${cartController}orders`, BACKEND_URL)
  return getRequest<OrdersListResponse>(fetchURL.href, axiosConfig)
}

export async function getOrdersByEmail(email: string, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(
    `${cartController}orders/by-email/${email}`,
    BACKEND_URL,
  )
  return getRequest<OrdersListResponse>(fetchURL.href, axiosConfig)
}

export async function getOrderByNumber(orderNumber: string, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(
    `${cartController}orders/${orderNumber}`,
    BACKEND_URL,
  )
  return getRequest<OrderResponse>(fetchURL.href, axiosConfig)
}

export async function getOrderByPaymentReference(paymentReference: string, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(
    `${cartController}orders/by-payment-reference/${paymentReference}`,
    BACKEND_URL,
  )
  return getRequest<OrderResponse>(fetchURL.href, axiosConfig)
};

export async function getAdminOrders(queryFilters?: AdminOrdersQueryFilters, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(`${cartController}admin-orders`, BACKEND_URL)
  const { columnFilters, sorting, pagination } = queryFilters || {}
  pagination
  && fetchURL.searchParams.set('pagination', JSON.stringify(pagination))
  columnFilters
  && fetchURL.searchParams.set('columnFilters', JSON.stringify(columnFilters))
  sorting && fetchURL.searchParams.set('sorting', JSON.stringify(sorting))
  return getRequest<AdminOrdersListResponse>(fetchURL.href, axiosConfig)
}

export async function getAdminOrderByNumber(orderNumber: string, axiosConfig: AxiosRequestConfig = {}) {
  const fetchURL = new URL(
    `${cartController}admin-orders/${orderNumber}`,
    BACKEND_URL,
  )
  return getRequest<OrderResponse>(fetchURL.href, axiosConfig)
}
